import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useBacktestRunsQuery,
  useBacktestReportQuery,
  useBacktestAuditQuery,
  useBacktestTradesQuery,
  type BacktestSource,
} from './tradingBacktestApi';
import type {
  BacktestEquityPoint,
  BacktestRunSummary,
  BacktestSignalBreakdown,
  BacktestTradeCycle,
} from './tradingBacktestMock';
import { Pager } from '../../components/Pager';
import './trading.css';

// 권위: data/reports/UNIMPLEMENTED_INVENTORY.md §4 — 화면 E 백테스트 결과.
//
// 라우팅:
//   /trading/backtest        → run 목록
//   /trading/backtest/:id    → run 상세 (동일 페이지에서 상세 노출)
//
// API 부재 (병렬 dashboard-api PR 진행 중) 시 mock fallback + 상단 안내 배너.
//
// 차트 라이브러리: 기존 LeadingSectors SVG fallback 패턴 차용 (recharts 미도입).
//   - tokens.css 변수만 사용. hex 0건.
//   - equity_curve: 라인 차트
//   - signal_breakdown: stacked horizontal bar

export default function TradingBacktestResultsPage(): JSX.Element {
  const params = useParams<{ id?: string }>();
  const selectedId = params.id;
  return selectedId ? <BacktestDetail runId={selectedId} /> : <BacktestRunList />;
}

// ── 목록 화면 ────────────────────────────────────────────────

function BacktestRunList(): JSX.Element {
  const { data, isLoading, isError, error } = useBacktestRunsQuery();
  const navigate = useNavigate();
  const rows = data?.rows ?? [];

  return (
    <div className="trading-page">
      <header className="trading-page__header">
        <h1 className="trading-page__title">백테스트 결과</h1>
        <span className="trading-page__subtitle">backtest · runs</span>
      </header>

      <SourceBanner source={data?.source} />

      {isError && (
        <div className="trading-page__notice" role="alert">
          <strong>오류 —</strong>
          {(error as Error)?.message ?? 'run 목록 조회 실패'}
        </div>
      )}

      <section className="trading-card" aria-label="백테스트 run 목록">
        <header className="trading-card__header">
          <h2 className="trading-card__title">RUN LIST</h2>
          <span className="trading-page__subtitle">{rows.length}건</span>
        </header>
        <div className="trading-card__body">
          <table className="trading-table">
            <thead>
              <tr>
                <th>라벨</th>
                <th>시작</th>
                <th>종료</th>
                <th className="num">최종자본</th>
                <th className="num">CAGR</th>
                <th className="num">Sharpe</th>
                <th className="num">MDD</th>
                <th className="num">승률</th>
                <th className="num">거래일</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={9} className="trading-table__empty">
                    불러오는 중…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="trading-table__empty">
                    백테스트 run 이 없습니다.
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="bt-row-clickable"
                  onClick={() => navigate(`/trading/backtest/${row.id}`)}
                >
                  <td>
                    <div>{row.label ?? row.id}</div>
                    <code style={{ color: 'var(--ink-faint)', fontSize: 'var(--text-xs)' }}>
                      {row.id}
                    </code>
                  </td>
                  <td>{row.startDate}</td>
                  <td>{row.endDate}</td>
                  <td className="num">{formatKrw(row.finalCapital)}</td>
                  <td className={`num ${pnlClass(row.cagr)}`}>{formatPct(row.cagr * 100)}</td>
                  <td className="num">{row.sharpe.toFixed(2)}</td>
                  <td className="num trading-num--down">-{formatPct(row.mdd * 100)}</td>
                  <td className="num">{formatPct(row.winRate * 100)}</td>
                  <td className="num">{row.tradingDays}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

// ── 상세 화면 ────────────────────────────────────────────────

type DetailTabId = 'summary' | 'trades' | 'audit';

const DETAIL_TABS: { id: DetailTabId; label: string }[] = [
  { id: 'summary', label: '요약' },
  { id: 'trades', label: '거래내역' },
  { id: 'audit', label: 'AUDIT LOG' },
];

function BacktestDetail({ runId }: { runId: string }): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useBacktestReportQuery(runId);
  const detail = data?.detail;
  const [activeTab, setActiveTab] = useState<DetailTabId>('summary');

  return (
    <div className="trading-page">
      <header className="trading-page__header">
        <h1 className="trading-page__title">
          백테스트 상세
          {detail && <span className="bt-detail__label"> · {detail.label}</span>}
        </h1>
        <button
          type="button"
          className="bt-back"
          onClick={() => navigate('/trading/backtest')}
        >
          ← 목록
        </button>
      </header>

      <SourceBanner source={data?.source} />

      {isError && (
        <div className="trading-page__notice" role="alert">
          <strong>오류 —</strong>
          {(error as Error)?.message ?? '백테스트 상세 조회 실패'}
        </div>
      )}

      {isLoading && (
        <div className="trading-table__empty">불러오는 중…</div>
      )}

      {detail && (
        <>
          <SummaryCards summary={detail} />

          <nav className="bt-tabs" role="tablist" aria-label="백테스트 상세 탭">
            {DETAIL_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                data-testid={`bt-tab-${tab.id}`}
                className={`bt-tab${activeTab === tab.id ? ' bt-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {activeTab === 'summary' && (
            <>
              <section className="trading-card" aria-label="자산곡선">
                <header className="trading-card__header">
                  <h2 className="trading-card__title">EQUITY CURVE</h2>
                  <span className="trading-page__subtitle">
                    {detail.startDate} → {detail.endDate}
                  </span>
                </header>
                <div className="trading-card__body">
                  <EquityCurveChart points={detail.equityCurve} />
                </div>
              </section>
              <section className="trading-card" aria-label="시그널 분포">
                <header className="trading-card__header">
                  <h2 className="trading-card__title">SIGNAL BREAKDOWN</h2>
                </header>
                <div className="trading-card__body">
                  <SignalBreakdownChart rows={detail.signalBreakdown} />
                </div>
              </section>
            </>
          )}

          {activeTab === 'trades' && <TradesSection runId={runId} />}

          {activeTab === 'audit' && <AuditSection runId={runId} />}
        </>
      )}
    </div>
  );
}

// ── Summary cards ────────────────────────────────────────────

function SummaryCards({ summary }: { summary: BacktestRunSummary }): JSX.Element {
  return (
    <section className="trading-kpi-row" aria-label="요약 지표">
      <div className="trading-kpi">
        <span className="trading-kpi__label">CAGR</span>
        <span className={`trading-kpi__value ${pnlClass(summary.cagr)}`}>
          {formatPct(summary.cagr * 100)}
        </span>
      </div>
      <div className="trading-kpi">
        <span className="trading-kpi__label">Sharpe</span>
        <span className="trading-kpi__value">{summary.sharpe.toFixed(2)}</span>
      </div>
      <div className="trading-kpi">
        <span className="trading-kpi__label">MDD</span>
        <span className="trading-kpi__value trading-num--down">
          -{formatPct(summary.mdd * 100)}
        </span>
      </div>
      <div className="trading-kpi">
        <span className="trading-kpi__label">승률</span>
        <span className="trading-kpi__value">{formatPct(summary.winRate * 100)}</span>
      </div>
      <div className="trading-kpi">
        <span className="trading-kpi__label">최종자본</span>
        <span className="trading-kpi__value">{formatKrw(summary.finalCapital)}</span>
      </div>
    </section>
  );
}

// ── Equity curve (SVG) ──────────────────────────────────────

const EQ_VIEW_W = 1100;
const EQ_VIEW_H = 280;
const EQ_PAD = { top: 16, right: 16, bottom: 28, left: 72 };

function EquityCurveChart({ points }: { points: BacktestEquityPoint[] }): JSX.Element {
  const { yMin, yMax, path } = useMemo(() => {
    if (points.length === 0) {
      return { yMin: 0, yMax: 1, path: '' };
    }
    let mn = Infinity;
    let mx = -Infinity;
    for (const p of points) {
      if (p.equity < mn) mn = p.equity;
      if (p.equity > mx) mx = p.equity;
    }
    if (mn === mx) {
      mn = mn * 0.95;
      mx = mx * 1.05;
    }
    const plotW = EQ_VIEW_W - EQ_PAD.left - EQ_PAD.right;
    const plotH = EQ_VIEW_H - EQ_PAD.top - EQ_PAD.bottom;
    const span = mx - mn || 1;
    const xOf = (i: number): number =>
      EQ_PAD.left + (i / Math.max(1, points.length - 1)) * plotW;
    const yOf = (v: number): number =>
      EQ_PAD.top + plotH - ((v - mn) / span) * plotH;
    const d = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(i).toFixed(1)} ${yOf(p.equity).toFixed(1)}`)
      .join(' ');
    return { yMin: mn, yMax: mx, path: d };
  }, [points]);

  if (points.length === 0) {
    return <div className="trading-table__empty">자산곡선 데이터 없음</div>;
  }

  const yTicks = [yMin, (yMin + yMax) / 2, yMax];
  const plotW = EQ_VIEW_W - EQ_PAD.left - EQ_PAD.right;
  const plotH = EQ_VIEW_H - EQ_PAD.top - EQ_PAD.bottom;
  const yOf = (v: number): number =>
    EQ_PAD.top + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH;

  return (
    <svg
      data-testid="bt-equity-chart"
      viewBox={`0 0 ${EQ_VIEW_W} ${EQ_VIEW_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="자산곡선"
      style={{ width: '100%', height: 280 }}
    >
      {yTicks.map((t, i) => (
        <g key={`yt-${i}`}>
          <line
            x1={EQ_PAD.left}
            y1={yOf(t)}
            x2={EQ_PAD.left + plotW}
            y2={yOf(t)}
            stroke="var(--seal-line-soft)"
            strokeWidth={1}
          />
          <text
            x={EQ_PAD.left - 6}
            y={yOf(t)}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize={10}
            fontFamily="var(--font-mono)"
            fill="var(--ink-faint)"
          >
            {formatKrwShort(t)}
          </text>
        </g>
      ))}
      <line
        x1={EQ_PAD.left}
        y1={EQ_PAD.top}
        x2={EQ_PAD.left}
        y2={EQ_PAD.top + plotH}
        stroke="var(--seal-line-bold)"
        strokeWidth={1}
      />
      <line
        x1={EQ_PAD.left}
        y1={EQ_PAD.top + plotH}
        x2={EQ_PAD.left + plotW}
        y2={EQ_PAD.top + plotH}
        stroke="var(--seal-line-bold)"
        strokeWidth={1}
      />
      <path d={path} fill="none" stroke="var(--accent)" strokeWidth={1.5} />
    </svg>
  );
}

// ── Signal breakdown (horizontal bar) ───────────────────────

function SignalBreakdownChart({
  rows,
}: {
  rows: BacktestSignalBreakdown[];
}): JSX.Element {
  if (rows.length === 0) {
    return <div className="trading-table__empty">시그널 분포 데이터 없음</div>;
  }
  return (
    <ul className="bt-breakdown" data-testid="bt-breakdown">
      {rows.map((r) => {
        const total = r.generated || 1;
        const execPct = (r.executed / total) * 100;
        const queuedOnlyPct = (Math.max(0, r.queued - r.executed) / total) * 100;
        const droppedPct = (r.dropped / total) * 100;
        return (
          <li key={r.kind} className="bt-breakdown__row">
            <span className="bt-breakdown__kind">{r.kind}</span>
            <div className="bt-breakdown__bar" aria-label={`${r.kind} 분포`}>
              <span
                className="bt-breakdown__seg bt-breakdown__seg--ok"
                style={{ width: `${execPct}%` }}
                title={`executed ${r.executed}`}
              />
              <span
                className="bt-breakdown__seg bt-breakdown__seg--warn"
                style={{ width: `${queuedOnlyPct}%` }}
                title={`queued-only ${Math.max(0, r.queued - r.executed)}`}
              />
              <span
                className="bt-breakdown__seg bt-breakdown__seg--danger"
                style={{ width: `${droppedPct}%` }}
                title={`dropped ${r.dropped}`}
              />
            </div>
            <span className="bt-breakdown__counts">
              <span className="trading-num--up">{r.executed}</span>
              <span className="bt-breakdown__sep">/</span>
              <span>{r.queued}</span>
              <span className="bt-breakdown__sep">/</span>
              <span className="trading-num--down">{r.dropped}</span>
              <span className="bt-breakdown__sep">·</span>
              <span>{r.generated}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ── Trades section (sort + filter + pagination) ─────────────
// 사용자 통찰: KRW PnL 정렬은 우량주(시총 큰 종목) cohort 를 누락한다.
// → 기본 정렬은 return_pct desc (절대 KRW 아님). footer 에 짧게 언급.

type TradesSortKey = 'net_pnl' | 'return_pct' | 'opened_at';

const TRADES_PAGE_SIZE = 50;

function TradesSection({ runId }: { runId: string }): JSX.Element {
  // phase 1: 서버는 전체 cycle 을 그대로 내려준다 (필터/정렬은 client 측).
  // phase 2 에서 서버 정렬로 이동할 때는 query 인자 추가하면 됨.
  const { data, isLoading, isError, error } = useBacktestTradesQuery(runId);

  // 기본 정렬: opened_at desc (최신 진입 먼저 — 시간 흐름 검토). KRW 정렬 우량주 누락
  // 통찰은 footer 안내문으로 유지.
  const [sortKey, setSortKey] = useState<TradesSortKey>('opened_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState<string[]>([]);
  const [page, setPage] = useState(0);

  const allTrades = data?.page.trades ?? [];
  // 서버가 알려주는 total — page slice 만 내려오는 경우 대비. 현 phase 는 전체 응답이
  // 한 번에 오므로 length 와 동일하지만 phase 2 (서버 페이지네이션) 호환.
  const totalCycles = data?.page.totalCycles ?? allTrades.length;

  // sector 후보 — 데이터에 등장하는 sector 만 노출.
  const sectorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of allTrades) {
      if (t.sector) set.add(t.sector);
    }
    return Array.from(set).sort();
  }, [allTrades]);

  const filtered = useMemo(() => {
    const q = symbolFilter.trim();
    const qLower = q.toLowerCase();
    return allTrades.filter((t) => {
      if (q) {
        // 종목코드 + 종목명 둘 다 contains 매칭 (한글/영문 양쪽 OK).
        const codeMatch = t.symbol.toLowerCase().includes(qLower);
        const nameMatch = (t.symbolName ?? '').toLowerCase().includes(qLower);
        if (!codeMatch && !nameMatch) return false;
      }
      if (sectorFilter.length > 0 && !sectorFilter.includes(t.sector)) return false;
      return true;
    });
  }, [allTrades, symbolFilter, sectorFilter]);

  const sorted = useMemo(() => {
    const arr = filtered.slice();
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av: number;
      let bv: number;
      if (sortKey === 'net_pnl') {
        av = a.realizedPnl;
        bv = b.realizedPnl;
      } else if (sortKey === 'return_pct') {
        av = a.returnPct;
        bv = b.returnPct;
      } else {
        av = Date.parse(a.openedAt) || 0;
        bv = Date.parse(b.openedAt) || 0;
      }
      if (av === bv) return 0;
      return av < bv ? -1 * dir : 1 * dir;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / TRADES_PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = sorted.slice(
    safePage * TRADES_PAGE_SIZE,
    safePage * TRADES_PAGE_SIZE + TRADES_PAGE_SIZE,
  );

  const handleSortToggle = (key: TradesSortKey): void => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(0);
  };

  const toggleSector = (s: string): void => {
    setSectorFilter((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
    setPage(0);
  };

  return (
    <section className="trading-card" aria-label="거래내역">
      <header className="trading-card__header">
        <h2 className="trading-card__title">TRADES</h2>
        <span
          className="trading-page__subtitle bt-trades-total"
          data-testid="bt-trades-total"
        >
          {filteredHint(totalCycles, sorted.length, allTrades.length)}
          <span className="bt-trades-total__sep"> · </span>
          {totalPages > 1 ? `${safePage + 1} / ${totalPages} 페이지` : '1 페이지'}
        </span>
      </header>
      <div className="trading-card__body">
        <div className="trading-filter" role="group" aria-label="거래내역 필터">
          <label className="trading-filter__field">
            <span>종목 검색</span>
            <input
              value={symbolFilter}
              onChange={(e) => {
                setSymbolFilter(e.target.value);
                setPage(0);
              }}
              placeholder="005930 또는 삼성전자"
            />
          </label>
          <div className="trading-filter__field bt-trades-sectors">
            <span>섹터 ({sectorFilter.length || '전체'})</span>
            <div className="bt-trades-sector-chips" role="group" aria-label="섹터 선택">
              {sectorOptions.length === 0 && (
                <span className="bt-trades-sector-empty">—</span>
              )}
              {sectorOptions.map((s) => {
                const active = sectorFilter.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    className={`bt-trades-sector-chip${active ? ' bt-trades-sector-chip--active' : ''}`}
                    onClick={() => toggleSector(s)}
                    aria-pressed={active}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <table className="trading-table bt-trades-table" style={{ marginTop: 'var(--space-2)' }}>
          <thead>
            <tr>
              <th>종목</th>
              <th>섹터</th>
              <th>
                <SortHeader
                  label="진입"
                  active={sortKey === 'opened_at'}
                  dir={sortDir}
                  onClick={() => handleSortToggle('opened_at')}
                />
              </th>
              <th>청산</th>
              <th className="num">수량</th>
              <th className="num">진입가</th>
              <th className="num">평균청산가</th>
              <th className="num">투입금</th>
              <th className="num">
                <SortHeader
                  label="net_pnl"
                  active={sortKey === 'net_pnl'}
                  dir={sortDir}
                  onClick={() => handleSortToggle('net_pnl')}
                />
              </th>
              <th className="num">
                <SortHeader
                  label="return%"
                  active={sortKey === 'return_pct'}
                  dir={sortDir}
                  onClick={() => handleSortToggle('return_pct')}
                />
              </th>
              <th className="num">보유일</th>
              <th>exit cascade</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={12} className="trading-table__empty">
                  불러오는 중…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={12} className="trading-table__empty">
                  trades 조회 실패 — {(error as Error)?.message ?? ''}
                </td>
              </tr>
            )}
            {!isLoading && !isError && pageItems.length === 0 && (
              <tr>
                <td colSpan={12} className="trading-table__empty" data-testid="bt-trades-empty">
                  조건에 해당하는 거래 cycle 이 없습니다.
                </td>
              </tr>
            )}
            {pageItems.map((t) => (
              <TradeRow key={t.cycleId} trade={t} />
            ))}
          </tbody>
        </table>

        <Pager page={safePage} pageSize={TRADES_PAGE_SIZE} total={sorted.length} onPageChange={setPage} />


        <p className="bt-trades-footnote" data-testid="bt-trades-footnote">
          ※ 절대 KRW 정렬(net_pnl desc)은 시총이 큰 우량주 cohort 가 누락되는 경향이
          있습니다 — 분포 확인 시 <strong>return% 정렬</strong>을 권장합니다.
        </p>
      </div>
    </section>
  );
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
}): JSX.Element {
  const arrow = active ? (dir === 'desc' ? ' ↓' : ' ↑') : '';
  return (
    <button
      type="button"
      className={`bt-trades-sort${active ? ' bt-trades-sort--active' : ''}`}
      onClick={onClick}
      aria-sort={active ? (dir === 'desc' ? 'descending' : 'ascending') : 'none'}
    >
      {label}
      {arrow}
    </button>
  );
}

function TradeRow({ trade }: { trade: BacktestTradeCycle }): JSX.Element {
  return (
    <tr>
      <td>
        <div className="bt-trades-symbol">
          <code className="bt-trades-symbol__code">{trade.symbol}</code>
          {trade.symbolName ? (
            <span className="bt-trades-symbol__name">{trade.symbolName}</span>
          ) : null}
        </div>
      </td>
      <td>{trade.sector || '–'}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
        {formatDateShort(trade.openedAt)}
      </td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
        {trade.closedAt ? formatDateShort(trade.closedAt) : '미청산'}
      </td>
      <td className="num">{formatKrw(trade.qty)}</td>
      <td className="num">{formatPrice(trade.entryPrice)}</td>
      <td className="num">
        {trade.avgExitPrice == null ? '–' : formatPrice(trade.avgExitPrice)}
      </td>
      <td className="num">{formatKrw(trade.cost)}</td>
      <td className={`num ${pnlClass(trade.realizedPnl)}`}>
        {formatPnl(trade.realizedPnl)}
      </td>
      <td className={`num ${pnlClass(trade.returnPct)}`}>
        {formatPct(trade.returnPct)}
      </td>
      <td className="num">{trade.holdDays}</td>
      <td>
        {trade.exitCascade.length === 0 ? (
          <span style={{ color: 'var(--ink-faint)' }}>–</span>
        ) : (
          <span className="bt-cascade-chips">
            {trade.exitCascade.map((c, i) => (
              <span key={i} className={`bt-cascade-chip ${cascadeClass(c)}`}>
                {c}
              </span>
            ))}
          </span>
        )}
      </td>
    </tr>
  );
}

function cascadeClass(code: string): string {
  if (code.startsWith('stop_loss')) return 'bt-cascade-chip--danger';
  if (code.startsWith('tp_')) return 'bt-cascade-chip--ok';
  if (code.includes('exit')) return 'bt-cascade-chip--neutral';
  return 'bt-cascade-chip--neutral';
}

// ── Audit section (filter + pagination) ─────────────────────

function AuditSection({ runId }: { runId: string }): JSX.Element {
  const [symbol, setSymbol] = useState('');
  const [eventCode, setEventCode] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const query = useMemo(
    () => ({
      symbol: symbol || undefined,
      eventCode: eventCode || undefined,
      from: from || undefined,
      to: to || undefined,
      page,
      pageSize,
    }),
    [symbol, eventCode, from, to, page],
  );

  const { data, isLoading, isError } = useBacktestAuditQuery(runId, query);
  const items = data?.page.items ?? [];
  const total = data?.page.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section className="trading-card" aria-label="audit log">
      <header className="trading-card__header">
        <h2 className="trading-card__title">AUDIT LOG</h2>
        <span className="trading-page__subtitle">
          {total} 건 · {page + 1} / {totalPages}
        </span>
      </header>
      <div className="trading-card__body">
        <div className="trading-filter" role="group" aria-label="audit 필터">
          <label className="trading-filter__field">
            <span>종목</span>
            <input
              value={symbol}
              onChange={(e) => {
                setSymbol(e.target.value);
                setPage(0);
              }}
              placeholder="005930"
            />
          </label>
          <label className="trading-filter__field">
            <span>event_code prefix</span>
            <input
              value={eventCode}
              onChange={(e) => {
                setEventCode(e.target.value);
                setPage(0);
              }}
              placeholder="TRADING.SIGNAL."
            />
          </label>
          <label className="trading-filter__field">
            <span>from</span>
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(0);
              }}
            />
          </label>
          <label className="trading-filter__field">
            <span>to</span>
            <input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(0);
              }}
            />
          </label>
        </div>

        <table className="trading-table" style={{ marginTop: 'var(--space-2)' }}>
          <thead>
            <tr>
              <th>시각</th>
              <th>종목</th>
              <th>event_code</th>
              <th>요약</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={4} className="trading-table__empty">
                  불러오는 중…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={4} className="trading-table__empty">
                  audit 조회 실패
                </td>
              </tr>
            )}
            {!isLoading && !isError && items.length === 0 && (
              <tr>
                <td colSpan={4} className="trading-table__empty">
                  조건에 해당하는 audit 행이 없습니다.
                </td>
              </tr>
            )}
            {items.map((row, i) => (
              <tr key={`${row.occurredAt}-${row.symbol}-${i}`}>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                  {row.occurredAt}
                </td>
                <td>{row.symbol}</td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                  {row.eventCode}
                </td>
                <td>{row.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <Pager page={page} pageSize={pageSize} total={total} onPageChange={setPage} />

      </div>
    </section>
  );
}

// ── Source banner ────────────────────────────────────────────

function SourceBanner({ source }: { source: BacktestSource | undefined }): JSX.Element | null {
  if (source !== 'mock') return null;
  return (
    <div className="trading-page__notice" role="status" data-testid="bt-mock-banner">
      <strong>API 부재 —</strong>
      backtest endpoint 가 아직 배포되지 않아 mock 데이터를 표시합니다.
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────

function formatKrw(value: number): string {
  if (!Number.isFinite(value)) return '–';
  return new Intl.NumberFormat('ko-KR').format(Math.round(value));
}

function formatKrwShort(value: number): string {
  if (!Number.isFinite(value)) return '–';
  if (value >= 100_000_000) return `${(value / 100_000_000).toFixed(1)}억`;
  if (value >= 10_000) return `${(value / 10_000).toFixed(0)}만`;
  return new Intl.NumberFormat('ko-KR').format(Math.round(value));
}

function formatPct(value: number): string {
  return `${value.toFixed(2)}%`;
}

function formatPrice(value: number): string {
  if (!Number.isFinite(value)) return '–';
  // 가격은 정수 / 소수 둘 다 있을 수 있다 (호가단위 의존).
  if (Math.abs(value) >= 1000) {
    return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(value);
  }
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 2 }).format(value);
}

function formatPnl(value: number): string {
  if (!Number.isFinite(value)) return '–';
  const sign = value > 0 ? '+' : '';
  return `${sign}${new Intl.NumberFormat('ko-KR').format(Math.round(value))}`;
}

function formatDateShort(iso: string): string {
  // 2025-07-25T15:20:00+09:00 → 2025-07-25 15:20
  if (!iso) return '–';
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (m) return `${m[1]} ${m[2]}`;
  return iso;
}

function pnlClass(value: number): string {
  if (value > 0.0001) return 'trading-num--up';
  if (value < -0.0001) return 'trading-num--down';
  return 'trading-num--zero';
}

/**
 * 거래내역 총 건수 hint.
 * - 필터 미적용: "총 264 cycle"
 * - 필터 적용: "264 cycle 중 12건 표시"
 *
 * loaded: 현재 클라이언트가 받은 trades 길이 (phase 2 서버 페이지네이션 대비).
 *   loaded < totalCycles 인 경우 "264 cycle (12 로드됨)" 표기.
 */
function filteredHint(
  totalCycles: number,
  filteredLength: number,
  loaded: number,
): string {
  const totalLabel = `총 ${totalCycles} cycle`;
  if (loaded < totalCycles) {
    if (filteredLength === loaded) {
      return `${totalLabel} (${loaded} 로드됨)`;
    }
    return `${totalLabel} · ${loaded} 로드됨 중 ${filteredLength} 건 일치`;
  }
  if (filteredLength === totalCycles) {
    return totalLabel;
  }
  return `${totalLabel} 중 ${filteredLength} 건 일치`;
}

